<template lang="pug">
  .standalone-page
    sunset-admin-app-bar

    v-container(fluid pt-12).mt-5
      v-layout(row wrap)
        v-flex(xs12)
          v-card.mt-4
            v-card-title.headline Get User Data
            v-card-text
              v-form
                v-text-field(
                  v-model="userEmail"
                  label="Email"
                  placeholder="Enter user email"
                  outlined
                  dense
                  mb-2
                )
            v-card-actions
              v-spacer
              v-btn.get-user-id-btn(
                @click="handleGetUserData"
                :loading="isLoadingUserId"
                :disabled="!userEmail || isLoadingUserId"
              ).my-2.mr-2 Get User Data
            
            template(v-if="userIdResult")
              v-card-text
                v-layout(row wrap)
                  v-flex(xs12 mb-2)
                    .copyable-box(@click="copyToClipboard(userIdResult.user_id)")
                      .copyable-label User ID
                      .copyable-value
                        span {{ userIdResult.user_id }}
                        v-icon.copy-icon(large) mdi-content-copy
                  v-flex(xs12 mb-2)
                    .copyable-box(@click="copyToClipboard(userIdResult.name || '')")
                      .copyable-label Name
                      .copyable-value
                        span {{ userIdResult.name || 'N/A' }}
                        v-icon.copy-icon(large) mdi-content-copy
                  v-flex(xs12 mb-2)
                    .copyable-box(@click="copyToClipboard(userIdResult.email || userEmail)")
                      .copyable-label Email
                      .copyable-value
                        span {{ userIdResult.email || userEmail }}
                        v-icon.copy-icon(large) mdi-content-copy
                  v-flex(xs12)
                    .copyable-box(@click="copyToClipboard(userIdResult.txt_plan || '')")
                      .copyable-label Plan
                      .copyable-value
                        span {{ userIdResult.txt_plan || 'N/A' }}
                        v-icon.copy-icon(large) mdi-content-copy

          v-card.mt-4
            v-card-title.headline Get User Migration Data
            v-card-text
              v-form
                v-text-field(
                  v-model="migrationUserId"
                  label="User ID"
                  placeholder="Enter user ID"
                  outlined
                  dense
                  mb-2
                  type="number"
                )
            v-card-actions
              v-spacer
              v-btn.get-user-id-btn(
                @click="handleGetMigrationData"
                :loading="isLoadingMigrationData"
                :disabled="!migrationUserId || isLoadingMigrationData"
              ).my-2.mr-2 Get User Migration Data
            
            template(v-if="migrationDataResult")
              v-card-text
                template(v-if="!migrationDataResult.migrations || migrationDataResult.migrations.length === 0")
                  .migration-no-data
                    | No migration records found for this user. The user may not have been migrated yet.
                template(v-else)
                  .migration-section.mb-4(
                    v-for="(migration, index) in migrationDataResult.migrations"
                    :key="index"
                  )
                    .migration-header.mb-3
                      v-layout(row wrap)
                        v-flex(xs12 sm3 mb-2)
                          .migration-header-box
                            .migration-header-label User ID
                            .migration-header-value {{ migration.user_id }}
                        v-flex(xs12 sm3 mb-2)
                          .migration-header-box
                            .migration-header-label Migration Master ID
                            .migration-header-value {{ migration.master_id }}
                        v-flex(xs12 sm3 mb-2)
                          .migration-header-box
                            .migration-header-label Status
                            .migration-header-value {{ formatStatus(migration.status) }}
                        v-flex(xs12 sm3 mb-2)
                          .migration-header-box
                            .migration-header-label Finished Date
                            .migration-header-value {{ migration.finished_at || 'N/A' }}
                    .migration-body
                      template(v-if="migration.error")
                        .migration-error {{ migration.error }}
                      template(v-else-if="migration.record_counts && migration.record_counts.length > 0")
                        .migration-results
                          .migration-type-box(
                            v-for="(typeGroup, typeName) in groupByType(migration.record_counts)"
                            :key="typeName"
                          )
                            .migration-type-title {{ typeName }}:
                            .migration-type-stats
                              span.migration-success Successes: {{ typeGroup.successes }}
                              span.migration-separator  - 
                              span.migration-error-count Errors: {{ typeGroup.errors }}
                      template(v-else)
                        .migration-info No record counts available

          v-card.mt-4.login-as-user-card
            v-card-title.headline Login as User (Impersonation)
            v-card-text
              v-form
                v-text-field(
                  v-model="loginAsUserId"
                  label="User ID"
                  placeholder="Enter user ID"
                  outlined
                  dense
                  mb-2
                  type="number"
                )
            v-card-actions
              v-spacer
              v-btn.get-user-id-btn(
                @click="handleLoginAsUser"
                :loading="isLoadingLoginAs"
                :disabled="!loginAsUserId || isLoadingLoginAs"
              ).my-2.mr-2 Login as User

          v-card.mt-4
            v-card-title.headline API Endpoints
            v-card-text
              v-layout(column)
                v-flex(v-for="endpoint in endpoints" :key="endpoint.id" mb-4)
                  endpoint-card(
                    :endpoint="endpoint"
                  )

      v-snackbar(v-if="errorResponse" :value="!!errorResponse" color="error" top @input="clearErrorResponse()")
        | {{ errorResponse }}
      v-snackbar(v-model="showCopySuccess" color="success" top :timeout="2000")
        | Copied to clipboard!
</template>

<script>
import { mapActions } from 'vuex';
import SunsetAdminAppBar from '../components/AppBar';
import EndpointCard from '../components/EndpointCard';

export default {
  name: 'SunsetAdminDashboard',
  components: {
    SunsetAdminAppBar,
    EndpointCard,
  },
  data() {
    return {
      userEmail: '',
      isLoadingUserId: false,
      userIdResult: null,
      migrationUserId: '',
      isLoadingMigrationData: false,
      migrationDataResult: null,
      loginAsUserId: '',
      isLoadingLoginAs: false,
      endpoints: [
        {
          id: 'export-contacts',
          method: 'POST',
          url: '/export-contacts',
          description: 'Export Contacts',
          fields: [
            { name: 'user_id', label: 'User ID', placeholder: 'Enter user ID', type: 'number', required: true },
            {
              name: 'file_type',
              label: 'File Type',
              type: 'select',
              items: [
                { text: 'CSV', value: 'csv' },
                { text: 'XLSX', value: 'xlsx' },
              ],
            },
          ]
        },
        {
          id: 'export-events',
          method: 'POST',
          url: '/export-events',
          description: 'Export Events',
          fields: [
            { name: 'user_id', label: 'User ID', placeholder: 'Enter user ID', type: 'number', required: true },
            {
              name: 'file_type',
              label: 'File Type',
              type: 'select',
              items: [
                { text: 'CSV', value: 'csv' },
                { text: 'XLSX', value: 'xlsx' },
              ],
            },
          ]
        },
        {
          id: 'export-templates',
          method: 'POST',
          url: '/generate-export-email',
          description: 'Export Templates',
          fields: [
            { name: 'user_id', label: 'User ID', placeholder: 'Enter user ID', type: 'number', required: true },
          ]
        },
        {
          id: 'export-documents',
          method: 'POST',
          url: '/generate-export-email',
          description: 'Export Documents',
          fields: [
            { name: 'user_id', label: 'User ID', placeholder: 'Enter user ID', type: 'number', required: true },
          ]
        },
      ],
      showCopySuccess: false,
      errorResponse: null,
    };
  },
  methods: {
    ...mapActions('sunsetAdmin', [
      'getUserDataByEmail',
      'getUserMigrationData',
      'getUserById',
      'loginAsUser',
    ]),
    setErrorResponse(error) {
      this.errorResponse = error;
    },
    clearErrorResponse() {
      this.errorResponse = null;
    },
    async handleGetUserData() {
      if (!this.userEmail) {
        return;
      }

      this.isLoadingUserId = true;
      this.userIdResult = null;

      try {
        const response = await this.getUserDataByEmail(this.userEmail);
        this.userIdResult = {
          user_id: response.data.user_id,
          name: response.data.name || '',
          txt_plan: response.data.txt_plan || '',
          email: this.userEmail,
        };
      } catch (error) {
        const errorMessage = error && error.message ? error.message :
                          (error && error.data && error.data.error ? error.data.error : 'Failed to get user ID');
        this.setErrorResponse(errorMessage);
        this.userIdResult = null;
      } finally {
        this.isLoadingUserId = false;
      }
    },
    copyToClipboard(text) {
      if (!text) return;
      
      // Create a temporary textarea element
      const textarea = document.createElement('textarea');
      textarea.value = text.toString();
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand('copy');
        // Show a brief success message
        this.showCopySuccess = true;
        setTimeout(() => {
          this.showCopySuccess = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      } finally {
        document.body.removeChild(textarea);
      }
    },
    async handleGetMigrationData() {
      if (!this.migrationUserId) {
        return;
      }

      this.isLoadingMigrationData = true;
      this.migrationDataResult = null;

      try {
        const response = await this.getUserMigrationData(this.migrationUserId);
        this.migrationDataResult = response.data;
      } catch (error) {
        const errorMessage = error && error.message ? error.message :
                          (error && error.data && error.data.error ? error.data.error : 'Failed to get migration data');
        this.setErrorResponse(errorMessage);
        this.migrationDataResult = null;
      } finally {
        this.isLoadingMigrationData = false;
      }
    },
    formatStatus(status) {
      if (!status) return 'N/A';
      return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },
    groupByType(recordCounts) {
      const grouped = {};
      
      const pluralize = (word) => {
        const pluralMap = {
          'Contact': 'Contacts',
          'Activity': 'Activities',
          'Task': 'Tasks',
          'Template': 'Templates',
        };
        return pluralMap[word] || word;
      };
      
      recordCounts.forEach(record => {
        const type = record.type || '';
        // Extract the base type (e.g., "Contact successes" -> "Contact")
        const match = type.match(/^(.+?)\s+(successes|errors)$/i);
        let typeName = match ? match[1] : type;
        
        typeName = pluralize(typeName);
        
        if (!grouped[typeName]) {
          grouped[typeName] = { successes: 0, errors: 0 };
        }
        
        if (type.toLowerCase().includes('success')) {
          grouped[typeName].successes = record.count || 0;
        } else if (type.toLowerCase().includes('error')) {
          grouped[typeName].errors = record.count || 0;
        }
      });
      
      return grouped;
    },
    async handleLoginAsUser() {
      if (!this.loginAsUserId) {
        return;
      }

      const userId = parseInt(this.loginAsUserId, 10);
      this.isLoadingLoginAs = true;

      try {
        const loginResponse = await this.loginAsUser({
          userId: userId,
        });

        if (loginResponse && loginResponse.success && loginResponse.data) {
          const accountsJWT = loginResponse.data.accountsJWT;
          const crmJWT = loginResponse.data.crmJWT;
          
          const domain = process.env.VUE_APP_MAIN_DOMAIN;
          
          if (accountsJWT) {
            // Set LD_AUTH cookie (expires in 2 hours) - matches original flow
            const expires = new Date();
            expires.setTime(expires.getTime() + (2 * 60 * 60 * 1000)); // 2 hours
            document.cookie = `LD_AUTH=${accountsJWT}; expires=${expires.toUTCString()}; path=/; domain=${domain}`;
          }
          
          if (crmJWT) {
            const rl6Expires = new Date();
            rl6Expires.setTime(rl6Expires.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7 days
            document.cookie = `RL6Token=${crmJWT}; expires=${rl6Expires.toUTCString()}; path=/; domain=${domain}`;
          }
          
          const sunsetExpires = new Date();
          sunsetExpires.setTime(sunsetExpires.getTime() + (2 * 60 * 60 * 1000)); // 2 hours
          document.cookie = `sunset_bypass=1; expires=${sunsetExpires.toUTCString()}; path=/; domain=${domain}`;
          
          // Set UL_OFF cookie with 25 minute expiration
          const ulOffExpires = new Date();
          ulOffExpires.setTime(ulOffExpires.getTime() + (25 * 60 * 1000)); // 25 minutes
          document.cookie = `UL_OFF=true; expires=${ulOffExpires.toUTCString()}; path=/; domain=${domain}`;
          
          let v2Url = process.env.VUE_APP_CRM_URL;
          if (v2Url.includes('lvh.me') && v2Url.startsWith('https://')) {
            v2Url = v2Url.replace('https://', 'http://');
          }
          v2Url = v2Url.replace(/\/$/, '') + '/dashboard';
           
          const newWindow = window.open(v2Url, 'liondeskadmin');
          
        } else {
          this.setErrorResponse(loginResponse && loginResponse.error ? loginResponse.error : 'Failed to login as user');
        }
      } catch (error) {
        const errorMsg = error && error.message ? error.message :
                        (error && error.data && error.data.error ? error.data.error : 'Failed to login as user');
        this.setErrorResponse(errorMsg);
      } finally {
        this.isLoadingLoginAs = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.standalone-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.headline {
  font-size: 16px !important;
  font-weight: 600 !important;
  line-height: 18px !important;
  font-family: -apple-system, "system-ui", "Segoe UI", Roboto, "Open Sans", Ubuntu, "Droid Sans", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol" !important;
  color: #626262 !important;
}

.copyable-box {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #eeeeee;
    border-color: #bdbdbd;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: scale(0.98);
  }
}

.copyable-label {
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.copyable-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-family: monospace;
  color: #333;
  word-break: break-all;
  
  span {
    flex: 1;
    margin-right: 8px;
  }
  
  .copy-icon {
    opacity: 0.7;
    transition: all 0.2s ease;
    flex-shrink: 0;
    color: #666;
    font-size: 20px !important;
  }
  
  .copyable-box:hover & .copy-icon {
    opacity: 1;
    color: #1976d2;
    transform: scale(1.1);
  }
}

::v-deep .get-user-id-btn {
  background-color: rgb(94, 94, 94) !important;
  border-radius: 6px !important;
  padding: 12px 32px !important;
  color: white !important;
  box-shadow: none !important;
  transition: all 0.3s ease;
  height: 36px !important;
  
  .v-btn__content {
    font-family: -apple-system, "system-ui", "Segoe UI", Roboto, "Open Sans", Ubuntu, "Droid Sans", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-weight: 600 !important;
    font-size: 14px !important;
    text-transform: capitalize !important;
    color: white !important;
    line-height: 18px !important;
  }
  
  &:hover {
    background-color: rgb(94, 94, 94) !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6 !important;
  }
}

.migration-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background: #fafafa;
  margin-bottom: 24px;
}

.migration-header {
  margin-bottom: 16px;
}

.migration-header-box {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  height: 100%;
}

.migration-header-label {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.migration-header-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

.migration-body {
  margin-top: 16px;
}

.migration-results {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.migration-type-box {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 14px;
}

.migration-type-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.migration-type-stats {
  font-size: 13px;
  color: #555;
}

.migration-success {
  color: #2e7d32;
  font-weight: 500;
}

.migration-separator {
  color: #999;
  margin: 0 4px;
}

.migration-error-count {
  color: #d32f2f;
  font-weight: 500;
}

.migration-error {
  color: #d32f2f;
  font-weight: 500;
  padding: 12px;
  background: #ffebee;
  border-radius: 6px;
  border: 1px solid #ffcdd2;
}

.migration-info {
  color: #666;
  font-style: italic;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
}

.migration-no-data {
  color: #666;
  padding: 20px;
  background: #fff3cd;
  border-radius: 6px;
  border: 1px solid #ffc107;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
}

</style>

